import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaBriefcase, FaEye, FaClock, FaUser, FaCrown, FaCheckCircle } from 'react-icons/fa';
import api from '../../services/api';

const Visitors = ({ setActiveTab, onViewProfile }) => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [connectFeedback, setConnectFeedback] = useState({});

    // Helper to get correct image URL
    const getProfileImage = (img, gender) => {
        if (!img) {
            return gender === 'Female'
                ? 'https://randomuser.me/api/portraits/women/85.jpg'
                : 'https://randomuser.me/api/portraits/men/85.jpg';
        }

        if (img.startsWith('http') || img.startsWith('data:') || img.startsWith('blob:')) {
            return img;
        }

        const BASE_URL = api.defaults.baseURL;
        const baseUrl = BASE_URL.replace("/api", "");

        const cleanPath = img.startsWith('/') ? img : `/${img}`;
        return `${baseUrl}${cleanPath}`;
    };
    const validVisitors = profiles.filter(
        (p) => p._id || p.id || p.name || p.userId
    );

    useEffect(() => {
        const fetchVisitors = async () => {
            try {
                const response = await api.get('/visitors');
                setProfiles(response.data || []);
            } catch (error) {
                console.error('Error fetching visitors:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchVisitors();
    }, []);
    const hasActiveSubscription = profiles.some(
        (p) => p.hasSubscription === true
    );

    const handleConnect = (profileId, profileName) => {
        setConnectFeedback(prev => ({ ...prev, [profileId]: true }));

        setTimeout(() => {
            setConnectFeedback(prev => ({ ...prev, [profileId]: false }));
        }, 2000);
    };
    

    const handleUpgradeClick = () => {
        if (setActiveTab) {
            setActiveTab('subscription');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
                    <p className="mt-4 text-text-secondary">Loading visitors...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full mx-auto px-4 py-8">
            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-text-primary drop-shadow-sm mb-2">Profile Visitors</h1>
                        <p className="text-text-secondary">
                            People who viewed your profile
                            <span className="font-semibold text-primary-pink ml-1">
                                {validVisitors.length} visitors
                            </span>
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary-pink">
                                {validVisitors.length}
                            </div>
                            <div className="text-sm text-text-muted">Total Views</div>
                        </div>
                        <div className="h-8 w-px bg-[rgba(255,255,255,0.08)]"></div>
                        <div className="text-center">
                            {/* <div className="text-2xl font-bold text-text-primary drop-shadow-sm">
                                {profiles.length > 0 ?
                                    Math.round(profiles.reduce((sum, p) => sum + (p.age || 30), 0) / profiles.length)
                                    : 0}
                            </div>
                            <div className="text-sm text-text-muted">Avg Age</div> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Upgrade Banner */}
            {!hasActiveSubscription && (
                <div className="relative overflow-hidden bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[rgba(255,46,136,0.15)] via-glass-card to-glass-card backdrop-blur-xl rounded-2xl border border-[rgba(255,46,136,0.3)] p-6 mb-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                    {/* Glowing Accent */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary-pink/10 blur-[80px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/4"></div>

                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-5">
                            <div className="p-4 bg-gradient-to-br from-primary-grad-start to-primary-grad-end rounded-xl shadow-[0_0_15px_rgba(255,46,136,0.4)]">
                                <FaCrown className="text-white text-2xl drop-shadow-md" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-text-primary dark:text-white drop-shadow-sm mb-1 tracking-wide">
                                    Get more profile visitors!
                                </h3>
                                <p className="text-sm text-text-secondary max-w-lg font-medium">
                                    Premium members get <span className="text-primary-pink font-semibold">10x more profile views</span> and better match recommendations.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleUpgradeClick}
                            className="px-8 py-3.5 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white rounded-full font-bold hover:shadow-[0_0_20px_rgba(255,46,136,0.6)] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_15px_rgba(255,46,136,0.3)] whitespace-nowrap"
                        >
                            Upgrade to Premium
                        </button>
                    </div>
                </div>
            )}


            {/* Visitors List */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {validVisitors.length === 0 ? (
                    <div className="col-span-full bg-glass-card backdrop-blur-[20px] border border-glass-border shadow-glass rounded-2xl p-12 text-center">
                        <div className="text-5xl mb-4 text-text-muted">👁️</div>
                        <h3 className="text-xl font-semibold text-text-primary mb-2">No visitors yet</h3>
                        <p className="text-text-muted max-w-md mx-auto mb-6">
                            Your profile hasn't been viewed by anyone yet. Complete your profile to get more visibility.
                        </p>
                        <button
                            onClick={handleUpgradeClick}
                            className="px-8 py-3 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white rounded-full font-bold hover:shadow-[0_0_20px_rgba(255,46,136,0.6)] transition-colors"
                        >
                            Boost Profile Visibility
                        </button>
                    </div>
                ) : (
                    validVisitors.map(profile => {
                        const gender = profile.gender || 'Male';
                        const isConnected = connectFeedback[profile.id || profile._id];

                        return (
                            <div
                                key={profile.id || profile._id}
                                onClick={() => onViewProfile && onViewProfile(profile.id || profile._id)}
                                className="relative flex flex-col p-5 bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-[24px] hover:bg-[rgba(255,255,255,0.06)] hover:-translate-y-1 transition-all duration-300 cursor-pointer group shadow-glass-sm"
                            >
                                <div className="flex items-start gap-4">
                                    {/* Avatar */}
                                    <div className="relative shrink-0">
                                        <img
                                            src={getProfileImage(profile.image, gender)}
                                            alt={profile.name}
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = gender === 'Female'
                                                    ? 'https://randomuser.me/api/portraits/women/85.jpg'
                                                    : 'https://randomuser.me/api/portraits/men/85.jpg';
                                            }}
                                            className="w-16 h-16 rounded-full object-cover border-[3px] border-[#070B1A] shadow-depth group-hover:scale-105 transition-transform duration-300"
                                        />
                                        <div className="absolute bottom-0 right-0 w-4 h-4 bg-success-green border-[3px] border-[#070B1A] rounded-full flex items-center justify-center">
                                            {profile.isVerified && <FaCheckCircle size={8} className="text-white" />}
                                        </div>
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start">
                                            <div className="min-w-0 pr-2">
                                                <h4 className="font-bold text-text-primary dark:text-white text-base truncate flex items-center gap-1.5">
                                                    {profile.name || 'Unknown'}
                                                    {profile.isVerified ? (
                                                        <FaCheckCircle className="text-success-green min-w-[12px]" size={12} />
                                                    ) : profile.isPremium ? (
                                                        <FaCrown className="text-premium-gold min-w-[12px]" size={12} />
                                                    ) : null}
                                                </h4>
                                                <p className="text-xs text-text-secondary mt-0.5 truncate">
                                                    {profile.age ? `${profile.age} yrs, ` : ''}{profile.location || 'Not specified'}
                                                </p>
                                            </div>
                                            {/* Heart Icon from sketch */}
                                            <button className="text-text-muted hover:text-primary-pink transition-colors p-1 shrink-0">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                                            </button>
                                        </div>

                                        {/* Circular Percentage Progress */}
                                        <div className="flex items-center gap-2.5 mt-3">
                                            <div className="relative flex items-center justify-center w-9 h-9">
                                                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                                                    <path className="text-white/10" strokeWidth="3" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                                    <path className="text-primary-pink" strokeDasharray="75, 100" strokeWidth="3" strokeLinecap="round" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                                </svg>
                                                <span className="absolute text-[10px] font-bold text-text-primary dark:text-white">75%</span>
                                            </div>
                                            <span className="text-[11px] font-medium text-primary-pink tracking-wide">Match</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom Pills */}
                                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5 overflow-x-auto no-scrollbar">
                                    <span className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-md text-[10px] text-text-secondary whitespace-nowrap">
                                        {profile.job || "Professional"}
                                    </span>
                                    {profile.time && (
                                        <span className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-md text-[10px] text-text-secondary whitespace-nowrap flex items-center gap-1">
                                            <FaClock size={8} /> {profile.time}
                                        </span>
                                    )}
                                    {profile.viewCount && profile.viewCount > 1 && (
                                        <span className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-md text-[10px] text-text-secondary whitespace-nowrap">
                                            Viewed {profile.viewCount}x
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* View Stats */}
            {profiles.length > 0 && (
                <div className="mt-8 bg-[rgba(255,255,255,0.03)] rounded-lg border border-glass-border p-6">
                    <div className="flex items-start gap-4">
                        <div className="p-2 bg-[rgba(255,46,136,0.15)] rounded-lg">
                            <FaEye className="text-primary-pink text-xl" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-text-primary drop-shadow-sm mb-2">Profile Visibility Insights</h3>
                            <p className="text-text-secondary text-sm">
                                Your profile has been viewed {profiles.length} times in the last 30 days.
                                Completing your profile details can increase visibility by up to 300%.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Visitors;